import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Fade,
} from '@mui/material';
import SouthIcon from '@mui/icons-material/South';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import useSparks from '../hooks/useSparks';
import { getSparkWordSet } from '../utils/nlpUtils';

// Splits text into segments, wrapping matched words in the skewed red-box treatment.
function renderHighlighted(text, wordSet) {
  if (!wordSet || wordSet.size === 0) return text;
  const tokens = text.split(/(\s+)/);
  return tokens.map((token, i) => {
    const clean = token.toLowerCase().replace(/[^a-z]/g, '');
    if (clean && wordSet.has(clean)) {
      return (
        <span key={i} style={{ display: 'inline-block', position: 'relative', padding: '2px 6px', color: '#FFFEED', verticalAlign: 'baseline' }}>
          <span style={{ position: 'absolute', inset: 0, background: '#FF371D', clipPath: 'polygon(0% 3%, 100% 0%, 99% 97%, 1% 100%)' }} />
          <span style={{ position: 'relative', zIndex: 1 }}>{token}</span>
        </span>
      );
    }
    return token;
  });
}


const timeframeOptions = [
  { value: 'already', label: 'Already have' },
  { value: 'not-yet', label: 'Not yet' },
  { value: 'week', label: 'A week' },
  { value: 'month', label: 'A month' },
  { value: 'year', label: 'A year' },
];

const timeframeLabelMap = {
  already: 'Already have',
  'not-yet': 'Not yet',
  week: 'A week',
  month: 'A month',
  year: 'A year',
};

function EditSparkScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { getSparkById, updateSpark, deleteSpark, loading } = useSparks();
  const spark = getSparkById(id);
  const topWordsSet = useMemo(() => getSparkWordSet(spark), [spark]);

  const isEditRoute = location.pathname.startsWith('/edit/');
  const [editing, setEditing] = useState(isEditRoute);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [formData, setFormData] = useState({
    moment: '',
    intensity: null,
    tellingYou: '',
    compelledTo: '',
    timeframe: null,
    completed: false,
  });

  // Load spark data when component mounts or id changes
  useEffect(() => {
    if (!loading && id) {
      const spark = getSparkById(id);
      if (spark) {
        setFormData({
          moment: spark.moment || spark.trigger || '',
          intensity: spark.intensity ?? spark.rating ?? null,
          tellingYou: spark.tellingYou || '',
          compelledTo: spark.compelledTo || spark.response || '',
          timeframe: spark.timeframe ?? null,
          completed: typeof spark.completed === 'boolean' ? spark.completed : false,
        });
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [id, loading, getSparkById, navigate]);

  // Sync editing state if route changes
  useEffect(() => {
    setEditing(isEditRoute);
    setConfirmDelete(false);
  }, [isEditRoute]);

  const handleSave = () => {
    updateSpark(id, {
      moment: formData.moment,
      intensity: formData.intensity,
      tellingYou: formData.tellingYou || null,
      compelledTo: formData.compelledTo || null,
      timeframe: formData.timeframe,
      completed: formData.timeframe === 'already',
    });
    setFormData((prev) => ({
      ...prev,
      completed: prev.timeframe === 'already',
    }));
    setEditing(false);
    setConfirmDelete(false);
  };

  const handleBack = () => {
    if (editing && !isEditRoute) {
      // Was viewing, entered edit — go back to view
      setEditing(false);
      setConfirmDelete(false);
    } else {
      navigate('/');
    }
  };

  const handleEdit = () => {
    setEditing(true);
    setConfirmDelete(false);
  };

  const handleDelete = () => {
    if (confirmDelete) {
      deleteSpark(id);
      navigate('/', { replace: true });
    } else {
      setConfirmDelete(true);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pl: 1,
          pr: 2,
          py: 1,
          flexShrink: 0,
        }}
      >
        <Button
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
          sx={{
            borderRadius: '100px',
            bgcolor: '#EDE9E3',
            color: 'text.primary',
            px: 2,
            py: 1,
            minHeight: 0,
            fontWeight: 500,
            fontSize: '14px',
            letterSpacing: '0.1px',
            textTransform: 'none',
            '&:hover': { bgcolor: '#E5E1DA' },
          }}
        >
          Back
        </Button>
        {!editing && (
          <IconButton
            onClick={handleEdit}
            aria-label="Edit spark"
            sx={{
              bgcolor: '#EDE9E3',
              color: 'text.primary',
              width: 40,
              height: 40,
              minHeight: 0,
              minWidth: 0,
              '&:hover': { bgcolor: '#E5E1DA' },
            }}
          >
            <EditIcon />
          </IconButton>
        )}
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          px: 2,
          pb: 2,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          WebkitOverflowScrolling: 'touch',
          minHeight: 0,
        }}
      >
        <Fade in timeout={300}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: editing ? '0 0 auto' : 1, pt: editing ? 0 : '32px' }}>
            {editing ? (
              /* ===== EDIT MODE ===== */
              <>
                {/* Describe your inspiration */}
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 500, color: '#EDE9E3' }}
                  >
                    Describe your Inspiration
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={6}
                    value={formData.moment}
                    onChange={(e) =>
                      setFormData({ ...formData, moment: e.target.value })
                    }
                    placeholder="What inspired you?"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'background.paper',
                      },
                    }}
                  />
                </Box>

                {/* What are you compelled to do? */}
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 500, color: '#EDE9E3' }}
                  >
                    What are you compelled to do?
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    maxRows={6}
                    placeholder="Do my own artwork"
                    value={formData.compelledTo}
                    onChange={(e) =>
                      setFormData({ ...formData, compelledTo: e.target.value })
                    }
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'background.paper',
                      },
                    }}
                  />
                </Box>

                {/* Timeframe */}
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1.5, fontWeight: 500, color: '#EDE9E3' }}
                  >
                    When do you want to take action?
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {timeframeOptions.map((option) => {
                      const isSelected = formData.timeframe === option.value;
                      return (
                        <Chip
                          key={option.value}
                          label={option.label}
                          onClick={() =>
                            setFormData({
                              ...formData,
                              timeframe: isSelected ? null : option.value,
                            })
                          }
                          variant={isSelected ? 'filled' : 'outlined'}
                          sx={{
                            bgcolor: isSelected ? 'primary.main' : 'transparent',
                            color: isSelected ? 'primary.contrastText' : '#EDE9E3',
                            borderColor: isSelected ? 'primary.main' : '#EDE9E3',
                            '& .MuiChip-label': { color: isSelected ? 'primary.contrastText' : '#EDE9E3' },
                            '&:hover': {
                              bgcolor: isSelected ? 'primary.dark' : 'rgba(237, 233, 227, 0.1)',
                            },
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>

                {/* Delete */}
                <Box sx={{ pt: 2 }}>
                  <Button
                    variant="text"
                    color="error"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={handleDelete}
                    fullWidth
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    {confirmDelete ? 'Tap again to confirm delete' : 'Delete spark'}
                  </Button>
                </Box>
              </>
            ) : (
              /* ===== VIEW (READ) MODE ===== */
              <>
                {/* Top text: moment (no action) or moment label (has action) */}
                {formData.compelledTo ? (
                  <Typography
                    variant="body1"
                    sx={{ fontSize: '24px', lineHeight: '32px', fontWeight: 400, color: '#EDE9E3' }}
                  >
                    {renderHighlighted(formData.moment, topWordsSet)}
                  </Typography>
                ) : (() => {
                  const len = formData.moment.length;
                  const isShort = len < 60;
                  const isMedium = len >= 60 && len < 140;
                  const fvs = isShort
                    ? "'wght' 800, 'wdth' 62, 'slnt' -10, 'GRAD' 0, 'opsz' 40"
                    : isMedium
                    ? "'wght' 481, 'wdth' 100, 'slnt' -10, 'GRAD' 150, 'opsz' 32"
                    : "'wght' 400, 'wdth' 83, 'slnt' 0, 'GRAD' -132, 'opsz' 28";
                  return (
                    <p
                      style={{
                        flex: 1,
                        margin: 0,
                        fontFamily: '"Roboto Flex", sans-serif',
                        fontWeight: isShort ? 800 : isMedium ? 481 : 400,
                        fontStyle: (isShort || isMedium) ? 'oblique 10deg' : 'normal',
                        fontSize: isShort ? '64px' : isMedium ? '48px' : '40px',
                        lineHeight: 'normal',
                        color: '#EDE9E3',
                        fontVariationSettings: fvs,
                      }}
                    >
                      {renderHighlighted(formData.moment, topWordsSet)}
                    </p>
                  );
                })()}

                {/* Down arrow separator */}
                <SouthIcon sx={{ fontSize: 24, color: '#B93200' }} />

                {/* Compelled to — hero text, size + variation scales with length */}
                {formData.compelledTo && (() => {
                  const len = formData.compelledTo.length;
                  const isShort = len < 60;
                  const isMedium = len >= 60 && len < 140;
                  const fvs = isShort
                    ? "'wght' 800, 'wdth' 62, 'slnt' -10, 'GRAD' 0, 'opsz' 40"
                    : isMedium
                    ? "'wght' 481, 'wdth' 100, 'slnt' -10, 'GRAD' 150, 'opsz' 32"
                    : "'wght' 400, 'wdth' 83, 'slnt' 0, 'GRAD' -132, 'opsz' 28";
                  return (
                    <p
                      style={{
                        flex: 1,
                        margin: 0,
                        fontFamily: '"Roboto Flex", sans-serif',
                        fontWeight: isShort ? 800 : isMedium ? 481 : 400,
                        fontStyle: (isShort || isMedium) ? 'oblique 10deg' : 'normal',
                        fontSize: isShort ? '64px' : isMedium ? '48px' : '40px',
                        lineHeight: 'normal',
                        color: '#EDE9E3',
                        fontVariationSettings: fvs,
                      }}
                    >
                      {renderHighlighted(formData.compelledTo, topWordsSet)}
                    </p>
                  );
                })()}

                {/* Placeholder when no compelledTo */}
                {!formData.compelledTo && (
                  <Typography
                    variant="body1"
                    sx={{ fontSize: '24px', lineHeight: '32px', fontWeight: 400, color: '#EDE9E3' }}
                  >
                    *just a spark*
                  </Typography>
                )}

                {/* Bottom: date + chips */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {spark && (
                    <Box>
                      <Typography variant="body1" sx={{ color: '#EDE9E3', lineHeight: '24px' }}>
                        Created
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#EDE9E3', lineHeight: '24px' }}>
                        {formatDate(spark.createdAt)}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {formData.timeframe && (
                      <Chip
                        label={timeframeLabelMap[formData.timeframe] || formData.timeframe}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderRadius: '8px',
                          borderColor: '#EDE9E3',
                          color: '#EDE9E3',
                          height: 32,
                          '& .MuiChip-label': { px: 1.5, fontSize: '14px', fontWeight: 500 },
                        }}
                      />
                    )}
                    {formData.completed && (
                      <Chip
                        icon={<CheckCircleIcon sx={{ fontSize: 18 }} />}
                        label="Completed"
                        size="small"
                        color="success"
                        sx={{
                          borderRadius: '8px',
                          height: 32,
                          '& .MuiChip-label': { px: 1.5, fontSize: '14px', fontWeight: 500 },
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </Fade>
      </Box>

      {/* Fixed bottom save button — only in edit mode */}
      {editing && (
        <Box
          sx={{
            px: 2,
            pt: 2,
            pb: 'max(24px, calc(env(safe-area-inset-bottom) + 16px))',
            flexShrink: 0,
            bgcolor: 'background.default',
          }}
        >
          <Button
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            onClick={handleSave}
            disabled={!formData.moment?.trim()}
            sx={{ py: 1.5 }}
          >
            Save
          </Button>
        </Box>
      )}
    </Box>
  );
}

export default EditSparkScreen;
